import { lazy, type PropsWithChildren, useEffect, useState } from 'react';
import { Outlet, useMatch } from 'react-router';

import SuspendedWithSpinner from '../components/SuspendedWithSpinner';
import { LiveGraphProvider } from '../features/graph/live/LiveGraphContext';
import { TransportProvider } from '../features/transports/TransportContext';
import { routes } from '../routes/routes';

const ConceptProvider = lazy(() =>
    import('../features/graph/concept/ConceptContext').then(module => ({
        default: module.ConceptProvider,
    }))
);

// Load the local showcase only on its first visit, then retain its independent
// state across navigation. The live providers stay outside this boundary.
const RetainedConceptState = ({ children }: PropsWithChildren) => {
    const active = Boolean(useMatch(routes.GRAPH_CONCEPT));
    const [visited, setVisited] = useState(active);
    useEffect(() => {
        if (active) {
            setVisited(true);
        }
    }, [active]);
    if (!active && !visited) {
        return children;
    }
    return (
        <SuspendedWithSpinner>
            <ConceptProvider>{children}</ConceptProvider>
        </SuspendedWithSpinner>
    );
};

// Application composition is the only layer that knows about both graph features.
export default function VariantStateLayout() {
    return (
        <TransportProvider>
            <LiveGraphProvider>
                <RetainedConceptState>
                    <Outlet />
                </RetainedConceptState>
            </LiveGraphProvider>
        </TransportProvider>
    );
}
